import React, { useState, useEffect, useRef, useCallback } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { IoMdArrowBack } from "react-icons/io";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../../../src/Config.js";
import Pagination from "../../../src/Component/Pagination";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

function StatutoryApprovalsListView({ isSidebarExpanded }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const listType = searchParams.get("list_type") || "all_approvals";
  const departmentId = searchParams.get("department_id") || "";
  const filterOption = searchParams.get("filter_option") || "all";
  const startDate = searchParams.get("start_date") || "";
  const endDate = searchParams.get("end_date") || "";
  const licenseType = searchParams.get("license_type") || "";

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [departmentName, setDepartmentName] = useState("");

  const containerRef = useRef(null);

  // const formatDate = (date) => {
  //   if (!date) return "NA";
  //   try {
  //     const options = { year: "numeric", month: "2-digit", day: "2-digit" };
  //     return new Date(date)
  //       .toLocaleDateString("en-CA", options)
  //       .replace(/-/g, "/");
  //   } catch (error) {
  //     return "NA";
  //   }
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";

    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "Invalid date";

    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}/${get("month")}/${get("year")}`;
  };

  const fetchData = async (
    currentPage,
    currentItemsPerPage,
    searchQuery = "",
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      if (!authKey) {
        console.error("No authentication key found");
        setLoading(false);
        return;
      }

      const params = {
        list_type: listType,
        page: currentPage,
        per_page: currentItemsPerPage,
      };

      if (departmentId && departmentId.trim() !== "") {
        params.department_id = departmentId;
      }

      if (licenseType && licenseType.trim() !== "") {
        params.license_type = licenseType;
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.query = searchQuery.trim();
      }

      console.log("Fetching Statutory Approvals list with params:", params);

      const response = await axios.get(
        `${API_URL}/api/kpi/statutory-approvals/list`,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
          params,
        },
      );

      console.log("API Response:", response.data);

      setReports(response.data.items || []);
      setTotalItems(response.data.total_items || 0);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching Statutory Approvals list:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
      setLoading(false);
      setReports([]);
      setTotalItems(0);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      setPage(1);
      fetchData(1, itemsPerPage, searchQuery);
    }, 300),
    [
      itemsPerPage,
      listType,
      departmentId,
      filterOption,
      startDate,
      endDate,
      licenseType,
    ],
  );

  useEffect(() => {
    fetchData(page, itemsPerPage, searchInput);
  }, [
    page,
    itemsPerPage,
    listType,
    departmentId,
    filterOption,
    startDate,
    endDate,
    licenseType,
  ]);

  const handlePaginationChange = (currentPage, newItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(newItemsPerPage);
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (value.trim() === "") {
      setPage(1);
      fetchData(1, itemsPerPage, "");
    } else {
      debouncedSearch(value);
    }
  };

  const handleSortpopup = () => {
    setIsSortPopup(!isSortPopup);
  };

  const closePopups = () => {
    setIsSortPopup(false);
  };

  const handleRefresh = () => {
    setSearchInput("");
    setPage(1);
    fetchData(1, itemsPerPage, "");
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "valid":
        return "#2E8B57";
      case "expired":
        return "#F81700";
      case "active":
        return "#2980B9";
      case "inactive":
        return "#6b7280";
      case "pending":
        return "#D4AC0D";
      default:
        return "black";
    }
  };

  const getListTypeDisplay = () => {
    switch (listType) {
      case "valid_approvals":
        return "Valid Approvals";
      case "expired_approvals":
        return "Expired Approvals";
      case "due_next_month":
        return "Due in Next Month";
      case "all_approvals":
        return "All Approvals";
      default:
        return "Statutory Approvals";
    }
  };

  const formatDates = (dateString) => {
    if (!dateString || typeof dateString !== "string") return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const formatter = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      return formatter.format(date);
    } catch (error) {
      return "";
    }
  };

  const getDaysIndicator = (report) => {
    if (report.current_status === "Expired") {
      return (
        <span style={{ color: "#F81700", fontWeight: "500" }}>
          {report.days_expired} days ago
        </span>
      );
    } else if (report.days_until_expiry <= 30) {
      return (
        <span style={{ color: "#D4AC0D", fontWeight: "500" }}>
          {report.days_until_expiry} days left
        </span>
      );
    } else {
      return (
        <span style={{ color: "#2E8B57", fontWeight: "500" }}>
          {report.days_until_expiry} days left
        </span>
      );
    }
  };

  return (
    <div
      className={`data-list-content-container page-content ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="data-list-content" ref={containerRef}>
        <div className="data-list-header">
          <div className="data-list-header-content">
            <div className="data-list-heading-content-left-side">
              {/* <Tooltip title="Back" arrow>
                <IoMdArrowBack className="back-company" onClick={handleBack} />
              </Tooltip> */}
              <span className="report-name">
                Statutory Approvals - {getListTypeDisplay()}
              </span>

              {departmentName && (
                <span
                  className="department-show"
                  style={{
                    color: "green",
                    marginLeft: "15px",
                    fontWeight: "500",
                  }}
                >
                  {departmentName}
                </span>
              )}

              {startDate && endDate && (
                <span
                  className="option-show"
                  style={{
                    color: "#ce9139ed",
                    marginLeft: "15px",
                    fontWeight: "500",
                  }}
                >
                  {formatDates(startDate)} - {formatDates(endDate)}
                </span>
              )}
            </div>

            <div className="data-list-heading-content-right-side">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search ..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  className="search-bar"
                />
                <IoSearchOutline className="search-bar-icon" />
              </div>

              <Tooltip title="Refresh List" arrow>
                <button className="refresh-button" onClick={handleRefresh}>
                  <TbRefresh className="refresh-icon" />
                </button>
              </Tooltip>

              <button className="sort-button" onClick={handleSortpopup}>
                <PiDotsThreeOutlineVerticalFill className="sort-icon" />
              </button>

              {isSortPopup && (
                <div className="sort-popup-container">
                  <div className="sort-popup-content">
                    <div className="sort-by">SORT BY</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="data-list-body" onClick={closePopups}>
          <div className="data-list-scroll-body">
            <div className="data-list-table">
              {loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <div>Loading...</div>
                </div>
              ) : reports.length > 0 ? (
                <table className="table multilevel-data">
                  <thead>
                    <tr>
                      <th className="center-alignment">SR. NO.</th>
                      <th>DEPARTMENT</th>
                      <th>LICENSE NAME</th>
                      <th>LICENSE TYPE</th>
                      <th>VALID FROM</th>
                      <th>VALID TO</th>
                      <th className="center-alignment">CURRENT STATUS</th>
                      <th className="center-alignment">DAYS UNTIL EXPIRY</th>
                      <th className="center-alignment">REMINDER (DAYS)</th>
                    </tr>
                  </thead>
                  <tbody className="data-list-scroll-bodys">
                    {reports.map((report, index) => (
                      <tr key={report.id} className="hoverable-row">
                        <td className="center-alignment">
                          {(page - 1) * itemsPerPage + index + 1}
                        </td>
                        <td>{report.department_name || "NA"}</td>
                        <td>{report.license_name || "NA"}</td>
                        <td>{report.license_type || "NA"}</td>
                        <td>{formatDate(report.valid_from)}</td>
                        <td>{formatDate(report.valid_to)}</td>
                        <td className="status-schedules">
                          <div
                            style={{
                              backgroundColor: getStatusColor(
                                report.current_status,
                              ),
                              width: "6vw",
                            }}
                          >
                            {report.current_status
                              ? report.current_status.charAt(0).toUpperCase() +
                                report.current_status.slice(1).toLowerCase()
                              : "NA"}
                          </div>
                        </td>
                        <td className="center-alignment">
                          {getDaysIndicator(report)}
                        </td>
                        <td className="center-alignment">
                          {report.reminder_before_days || "NA"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-users-container">
                  <div className="no-users-message">
                    No {getListTypeDisplay()} Records Found
                  </div>
                </div>
              )}
            </div>

            {totalItems > 0 && (
              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatutoryApprovalsListView;
